// Integration upgrades that turn flat EMR sections into View More fields of a
// new Flex section. Each one carries the old section's instruction and Skip
// into the View More entry of the same name, so a user's settings survive the
// switch (COM-561). This is the first copy only; after that the backend syncs
// the two sides on every switch (Support/IntegrationSwitch.py), so keep the
// aliases there in step.
//
// Adding an EMR: give its backend feature flag and the apiClient property the
// scan reads it from (saveIntegrationSwitch copies one onto the other), the
// hosts the new injector runs on, and an alias list for any View More field
// whose old flat section had a different name.
export const INTEGRATION_UPGRADES = [
  {
    // Prompt's old integration filled each section's comment box as its own
    // flat section ("ROM Comments"); the new one nests it under the section's
    // View More (ROM > ROM Comments).
    emr: 'prompt',
    feature: 'new_prompt_integration',
    property: 'newPromptIntegration',
    hosts: ['promptemr.com'],
    // The old integration scraped Patient Education's icons as a Multiple
    // Choice ("Patient Education") and its text box as "Patient Education #2";
    // the new View More field is that text box.
    aliases: {
      'Patient Education': ['Patient Education #2', 'Patient Education'],
    },
  },
];

/** The upgrade that applies to this scan, or null. */
export function integrationUpgradeFor(apiClient, url) {
  if (!url) return null;
  return (
    INTEGRATION_UPGRADES.find(
      (upgrade) =>
        apiClient?.[upgrade.property] &&
        upgrade.hosts.some((host) => url.includes(host)),
    ) || null
  );
}

const isSkip = (replyType) => {
  const value =
    replyType && typeof replyType === 'object'
      ? replyType['Existing Value']
      : replyType;
  return value === 'no_reply' || value === 'skip';
};

const parseFlexInstruction = (instruction) => {
  if (typeof instruction === 'string') {
    try {
      const parsed = JSON.parse(instruction);
      if (
        parsed &&
        typeof parsed === 'object' &&
        ('TopLevelInstruction' in parsed || 'SubSections' in parsed)
      ) {
        let subSections = parsed.SubSections || {};
        if (typeof subSections === 'string') {
          try {
            subSections = JSON.parse(subSections);
          } catch {
            subSections = {};
          }
        }
        return { ...parsed, SubSections: subSections };
      }
    } catch {
      // Plain text instruction
    }
    return { TopLevelInstruction: instruction, SubSections: {} };
  }
  if (instruction && typeof instruction === 'object') {
    return { ...instruction, SubSections: instruction.SubSections || {} };
  }
  return { TopLevelInstruction: '', SubSections: {} };
};

/**
 * One Flex section's instruction with every legacy flat section that matches
 * one of its View More names moved in. Returns the instruction unchanged
 * when there is nothing to carry.
 *
 * - Only names that are no longer scanned as their own section are legacy.
 * - A View More entry the user already saved is never overwritten.
 * - The flat section's Skip carries over even when it had no instruction.
 */
export function withLegacySubsectionInstructions(
  instruction,
  viewMoreNames,
  template,
  replyTypes,
  liveSectionKeys,
  aliases = {},
) {
  const flex = parseFlexInstruction(instruction);
  // An empty top level with SubSections makes the backend use the raw JSON as
  // the section's instruction; wait until the section has one.
  if (!String(flex.TopLevelInstruction || '').trim()) return instruction;
  const subSections = { ...flex.SubSections };
  let changed = false;

  const legacyFor = (key) => {
    const legacy = template?.[key];
    return {
      text:
        typeof legacy === 'string' && !legacy.trim().startsWith('{')
          ? legacy.trim()
          : '',
      skipped: isSkip(replyTypes?.[key]),
    };
  };

  (viewMoreNames || []).forEach((name) => {
    if (liveSectionKeys.has(name) || name in subSections) return;
    const { text, skipped } =
      (aliases[name] || [name])
        .filter((key) => !liveSectionKeys.has(key))
        .map(legacyFor)
        .find((legacy) => legacy.text || legacy.skipped) || {};
    if (!text && !skipped) return;
    subSections[name] = {
      Instruction: text,
      replyType: skipped ? 'no_reply' : 'overwrite',
    };
    changed = true;
  });

  if (!changed) return instruction;
  return JSON.stringify({
    TopLevelInstruction: flex.TopLevelInstruction || '',
    SubSections: subSections,
  });
}
